import PropTypes from "prop-types";

import TransportationMarketingBanner from "app/pages/.shared/TransportationMarketingBanner/TransportationMarketingBanner";
import { isValid } from "date-fns";
import { memo, useCallback, useRef, useEffect } from "react";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";

const IMG_SIZES = {
	small: { width: 710, height: 120 },
	medium: { width: 710, height: 120 },
	large: { width: 710, height: 120 },
	xlarge: { width: 710, height: 120 },
};

const CLOUDINARY_OPTIONS = ["c_fill"];

function FlightsFilterMarketingBanner({
	className,
	flightMarketingBanner = {},
	minUpgradePricesByCabinPerPerson = {},
	saleCode,
	onClickCabin,
	currentMerchCode,
}) {
	const { track } = useAnalytics();
	const bannerRef = useRef();

	const { cabin, startDate, endDate, saleCodeParamValue, merchCode } =
		flightMarketingBanner?.displayConditions || {};

	const isElementOnScreen = useOnScreen({ ref: bannerRef, triggerOnce: true });

	const isFlightsMarketingBannerNotExpired = () => {
		const todayTimestamp = new Date().getTime();

		const flightsMarketingBannerStartAtTimestamp = new Date(startDate).getTime();

		const flightsMarketingBannerEndAtTimestamp = new Date(endDate).getTime();

		if (!isValid(flightsMarketingBannerStartAtTimestamp)) {
			return false;
		} else if (
			!isValid(flightsMarketingBannerEndAtTimestamp) &&
			flightsMarketingBannerStartAtTimestamp <= todayTimestamp
		) {
			return true;
		}

		return (
			flightsMarketingBannerEndAtTimestamp >= todayTimestamp &&
			flightsMarketingBannerStartAtTimestamp <= todayTimestamp
		);
	};

	const isCabinTypeClassAvailable = Object.prototype.hasOwnProperty.call(
		minUpgradePricesByCabinPerPerson,
		cabin
	);

	const onCabinClassClick = useCallback(() => {
		onClickCabin(cabin);
		track("quote_click_banner", {
			cabinCode: cabin,
			saleCode: saleCodeParamValue,
		});
	}, [cabin, saleCodeParamValue]);

	const matchSaleCode =
		!saleCodeParamValue || (saleCodeParamValue && saleCode === saleCodeParamValue);

	const matchMerchCode = !merchCode || (merchCode && currentMerchCode === merchCode);

	const shouldShowBanner =
		isFlightsMarketingBannerNotExpired() &&
		matchSaleCode &&
		matchMerchCode &&
		(!cabin || isCabinTypeClassAvailable);

	useEffect(() => {
		if (isElementOnScreen && shouldShowBanner) {
			track("quote_view_banner", {
				cabinCode: cabin,
				saleCode: saleCodeParamValue,
			});
		}
	}, [isElementOnScreen, cabin, saleCodeParamValue, shouldShowBanner]);

	return (
		<div ref={bannerRef}>
			{shouldShowBanner && (
				<TransportationMarketingBanner
					disableClick={cabin ? false : true}
					className={className}
					imageSrc={flightMarketingBanner?.image}
					imageThumbnail={flightMarketingBanner?.thumbnail}
					imageCloudinarySizes={IMG_SIZES}
					imageCloudinaryOptions={CLOUDINARY_OPTIONS}
					onClick={onCabinClassClick}
				/>
			)}
		</div>
	);
}

FlightsFilterMarketingBanner.propTypes = {
	className: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	saleCode: PropTypes.string,
	flightMarketingBanner: PropTypes.object,
	minUpgradePricesByCabinPerPerson: PropTypes.object,
	onClickCabin: PropTypes.func,
	currentMerchCode: PropTypes.string,
};

export default memo(FlightsFilterMarketingBanner);
